import { User } from 'firebase/auth';
import { POSConfig } from '../services/pos/types';

// POS Provider Types
export type POSProvider = 'mock' | 'none';
// Future: 'inflo' | 'alegra' | 'toast' | 'square' when ready

export type UserRole = 'super_admin' | 'owner' | 'admin' | 'staff';

export const ROLE_PRIORITY: Record<UserRole, number> = {  
  super_admin: 5,
  owner: 4,
  admin: 3,
  staff: 2
};

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {  
  super_admin: 'Acceso completo a la plataforma: creación de restaurantes, configuración de procesadores de pago, gestión de usuarios y reportes. (Super Administrador)',
  owner: 'Acceso completo al restaurante: configuración, personal, menú y reportes. (Dueño)',
  admin: 'Puede gestionar configuración, personal y menú. Sin acceso a reportes. (Administrador)',
  staff: 'Acceso limitado: solo puede ver y actualizar mesas y pedidos asignados. (Personal de cocina y barra)'
};

export const ROLE_PERMISSIONS: Record<UserRole, { 
  canManageStaff: boolean;
  canManageSettings: boolean;
  canManageMenu: boolean;
  canManageTables: boolean;
  canViewReports: boolean;
  canManageInventory: boolean;
}> = {
  super_admin: {
    canManageStaff: true,
    canManageSettings: true,
    canManageMenu: true,
    canManageTables: true,
    canViewReports: true,
    canManageInventory: true
  },
  owner: {
    canManageStaff: true,
    canManageSettings: true,
    canManageMenu: true,
    canManageTables: true,
    canViewReports: true,
    canManageInventory: true
  },
  admin: {
    canManageStaff: true,
    canManageSettings: true,
    canManageMenu: true,
    canManageTables: true,
    canViewReports: true,
    canManageInventory: true
  },
  staff: {
    canManageStaff: false,
    canManageSettings: false,
    canManageMenu: true,
    canManageTables: false,
    canViewReports: false,
    canManageInventory: true
  }
};

export type LogSeverity = 'critical' | 'warning' | 'info' | 'debug';

export type LogAction = 
  | 'add_staff'
  | 'remove_staff'
  | 'update_role'
  | 'update_settings'
  | 'update_menu'
  | 'toggle_active'
  | 'update_logo'
  | 'login'
  | 'logout'
  | 'error';

export interface ActionLog {
  id?: string; // Firestore document ID
  type: LogAction;
  severity: LogSeverity;
  userId: string;
  userEmail: string;
  userRole: UserRole;
  restaurantId: string;
  details: Record<string, any>;
  timestamp: Date;
  archived: boolean;
  archivedAt?: Date;
}

// Firestore indexes configuration
export const FIRESTORE_INDEXES = {
  logs: [
    {
      collectionGroup: 'logs',
      queryScope: 'COLLECTION',
      fields: [
        { fieldPath: 'restaurantId', order: 'ASCENDING' },
        { fieldPath: 'timestamp', order: 'DESCENDING' }
      ]
    },
    {
      collectionGroup: 'logs',
      queryScope: 'COLLECTION',
      fields: [
        { fieldPath: 'type', order: 'ASCENDING' },
        { fieldPath: 'timestamp', order: 'DESCENDING' }
      ]
    },
    {
      collectionGroup: 'logs',
      queryScope: 'COLLECTION',
      fields: [
        { fieldPath: 'userId', order: 'ASCENDING' },
        { fieldPath: 'timestamp', order: 'DESCENDING' }
      ]
    },
    {
      collectionGroup: 'logs',
      queryScope: 'COLLECTION',
      fields: [
        { fieldPath: 'severity', order: 'ASCENDING' },
        { fieldPath: 'timestamp', order: 'DESCENDING' }
      ]
    }
  ]
};

// Log retention configuration
export const LOG_RETENTION = {
  critical: 365, // days
  warning: 90,
  info: 30,
  debug: 7
};

export interface UserProfile {
  id: string;
  email: string;
  displayName: string;
  role: UserRole;
  restaurantId: string;
  active: boolean;
  createdAt: Date;
  lastLogin?: Date;
}

export interface RestaurantSettings {
  // Location & Contact
  timezone: string;              // e.g., "America/Santo_Domingo"
  language: string;              // e.g., "es" or "en"
  
  // Financial Settings
  currency: string;              // e.g., "DOP", "USD"
  currencySymbol: string;        // e.g., "RD$", "$"
  taxRate: number;               // e.g., 18 (for 18% ITBIS)
  serviceCharge: number;         // Optional service charge %
  
  // Business Rules
  allowSplitBill: boolean;       // Enable split bill feature?
  allowTipping: boolean;         // Enable tipping?
  tipOptions: number[];          // e.g., [10, 15, 20]
  
  // Operational Settings
  autoCloseTableHours: number;   // Auto-close paid tables after X hours
  requireStaffRating: boolean;   // Force customers to rate staff?
}

export const DEFAULT_RESTAURANT_SETTINGS: RestaurantSettings = {
  timezone: 'America/Santo_Domingo',
  language: 'es',
  currency: 'DOP',
  currencySymbol: 'RD$',
  taxRate: 18,
  serviceCharge: 0,
  allowSplitBill: true,
  allowTipping: true,
  tipOptions: [10, 15, 20],
  autoCloseTableHours: 4,
  requireStaffRating: false,
};

// Payment Processor Types
export type PaymentProcessor = 'azul' | 'cardnet' | 'stripe' | 'none';

// Shared environment type for all processors
export type ProcessorEnvironment = 'sandbox' | 'test' | 'live' | 'production';

export interface AzulConfig {
  enabled: boolean;
  environment: ProcessorEnvironment;
  channel: string;
  merchantId: string;
  secretName?: string;        // GSM reference (optional)
  auth1?: string;             // Legacy credentials (optional)
  auth2?: string;             // Legacy credentials (optional)
}

export interface CardNetConfig {
  enabled: boolean;
  environment: ProcessorEnvironment;
  merchantId: string;
  secretName?: string;        // GSM reference (optional)
  apiSecret?: string;         // Legacy credentials (optional)
}

export interface StripeConfig {
  enabled: boolean;
  environment: ProcessorEnvironment;
  publicKey: string;        // This stays (it's public)
  merchantId?: string;
  secretName?: string;      // GSM reference for secret key (optional)
}


export interface Restaurant {
  // ========== METADATA ==========
  id?: string;                     // Firestore doc ID (optional for creation)
  createdAt: Date;
  updatedAt: Date;
  
  // ========== BASIC INFO ==========
  name: string;                    // Required, not optional
  active: boolean;
  logoUrl?: string;
  
  // Contact Info (optional)
  address?: string;
  phone?: string;
  email?: string;
  
  // ========== PAYMENT PROCESSOR ==========
  processor: PaymentProcessor;     // Required: 'stripe' | 'cardnet' | 'azul' | 'none'
  
  // Processor Configs (only ONE should be configured based on processor field)
  stripeConfig?: StripeConfig;
  cardnetConfig?: CardNetConfig;
  azulConfig?: AzulConfig;
  
  // ========== POS INTEGRATION (SUPER ADMIN ONLY) ==========
  integrationMode: 'api' | 'webhook' | 'polling' | 'none';  // CRITICAL: Set by super admin, inherited by mesas
  posConfig: POSConfig;            // Required, contains credentials and config
  
  // ========== BUSINESS SETTINGS ==========
  settings: RestaurantSettings;    // Required, contains all business rules
}

export interface RestaurantContextState {
  user: User | null;
  userProfile: UserProfile | null;
  restaurant: Restaurant | null;
  isLoading: boolean;
  error: string | null;
}

export interface RestaurantContextType extends RestaurantContextState {
  // Core actions
  refreshRestaurantData: () => Promise<void>;
  
  // Settings management
  updateRestaurantSettings: (settings: Partial<RestaurantSettings>) => Promise<void>;
  // Removed: updateMenuSettings (YAP doesn't manage menus)
  
  // Staff management
  addStaffMember: (email: string, role: UserRole) => Promise<void>;
  removeStaffMember: (userId: string) => Promise<void>;
  updateStaffRole: (userId: string, newRole: UserRole) => Promise<void>;
  
  // Restaurant management
  updateRestaurantName: (name: string) => Promise<void>;
  setRestaurantLogo: (logoUrl: string) => Promise<void>;
  toggleRestaurantActive: (active: boolean) => Promise<void>;
  
  // Permission checks
  hasPermission: (requiredRole: UserRole) => boolean;
  canManageStaff: () => boolean;
  canManageSettings: () => boolean;
  canManageMenu: () => boolean;
  canManageTables: () => boolean;
  canViewReports: () => boolean;
  canManageInventory: () => boolean;
  // Expose userRole for sidebar and route guards
  userRole: UserRole | null;
} 